import React, { useMemo } from 'react';

import { LinkPreviewHead } from '@shared/components/LinkPreviewHead';
import { parseDateRangeQueryParams } from '@shared/utils/dateRangeQueryParams';
import { useEventLocaleFormatter } from '@shared/core/localization/hooks/useEventLocaleFormatter';
import { useHotelStaticByJoyPlaceQuery } from '../../routes/HotelPDP3/HotelPDP3.graphql.generated';
import { buildCanonicalUrl, buildHotelOgDescription, buildHotelOgImageUrl, buildHotelOgTitle } from '@apps/stays/utils/hotelOgContent';

type StaysPdpHeadProps = Readonly<{
  joyPlaceId: string;
  eventId: string | null;
  checkIn: string | null;
  checkOut: string | null;
}>;

/**
 * SSR-safe head component for /stays/:joyPlaceId.
 *
 * Fetches static hotel catalog data during SSR (ssr: true) and renders Open Graph
 * + Twitter Card meta tags via react-helmet-async so link preview crawlers
 * (Slack, iMessage, WhatsApp, etc.) can display a rich unfurl.
 *
 * The rest of the PDP body stays client-only (NoSsr / EventHandleFetcher),
 * matching the GuestSiteHeadCsr pattern used by guest site pages.
 */
export const StaysPdpHead: React.FC<StaysPdpHeadProps> = ({ joyPlaceId, eventId, checkIn, checkOut }) => {
  const { data } = useHotelStaticByJoyPlaceQuery({
    variables: { idObject: { joyPlaceId } },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: true,
  });

  const property = data?.accommodationCatalogPropertyById;
  const { formatDateRange } = useEventLocaleFormatter();

  // All content derivation lives in hotelOgContent.ts, shared with the Share
  // Your Stay unfurl mock so the preview matches these tags.
  const ogData = useMemo(() => {
    if (!property) return null;

    const dateRange = parseDateRangeQueryParams(checkIn, checkOut);
    // Local Dates, formatted in the guest display locale: "Hotel Name – City, ST · Jul 17 - 19, 2026"
    const dateLabel = dateRange ? formatDateRange(dateRange.checkInDate, dateRange.checkOutDate) : undefined;

    return {
      imageUrl: buildHotelOgImageUrl(property),
      title: buildHotelOgTitle(property, dateLabel),
      description: buildHotelOgDescription(property),
      ogUrl: buildCanonicalUrl(joyPlaceId, eventId, dateRange?.checkIn ?? null, dateRange?.checkOut ?? null),
    };
  }, [property, joyPlaceId, eventId, checkIn, checkOut, formatDateRange]);

  if (!ogData) {
    return null;
  }

  return (
    <LinkPreviewHead
      url={ogData.ogUrl}
      title={ogData.title}
      imageUrl={ogData.imageUrl}
      description={ogData.description}
    />
  );
};
